// React
import React, { useEffect, useMemo, useRef, useState } from "react";

// MUI
import {
  CheckRounded,
  ContentCopyRounded,
  ErrorOutlineRounded,
  ReplayRounded,
  SubdirectoryArrowRightRounded,
} from "@mui/icons-material";
import { Tooltip } from "@mui/material";

// Third party imports
import { useTranslation } from "react-i18next";

// Components
import { copyToClipboard } from "components/ui/ContextMenu";

import AiMarkdown from "./AiMarkdown";
import AiBlock from "./blocks";
import { splitReply, stripMarkers } from "./blocks/blockUtils";

// "Ho cercato tra i clienti · Ho letto le note": one label per tool used, no repeats
const useToolsCaption = (toolsUsed) => {
  const { t } = useTranslation("aiAssistant");
  if (!toolsUsed?.length) return "";
  const labels = [...new Set(toolsUsed)].map((tool) =>
    t(`tools.${tool}`, { defaultValue: t("tools.other") })
  );
  return [...new Set(labels)].join(" · ");
};

function CopyButton({ text }) {
  const { t } = useTranslation("aiAssistant");
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const handleCopy = async () => {
    const ok = await copyToClipboard(text);
    if (!ok) return;
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1500);
  };

  const label = copied ? t("message.copied") : t("message.copy");
  return (
    <Tooltip title={label} placement="top">
      <button
        type="button"
        onClick={handleCopy}
        aria-label={label}
        className={`hover-action inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-2 focus-visible:outline-primary ${copied ? "is-active" : ""}`}
      >
        {copied ? (
          <CheckRounded sx={{ fontSize: 16 }} />
        ) : (
          <ContentCopyRounded sx={{ fontSize: 15 }} />
        )}
      </button>
    </Tooltip>
  );
}

// Follow-up questions under the last reply: one click sends them
function Suggestions({ items, onSuggest }) {
  const { t } = useTranslation("aiAssistant");
  return (
    <div role="group" aria-label={t("followUps.label")} className="mt-3 flex flex-wrap gap-2">
      {items.map((text, index) => (
        <button
          key={`${index}-${text}`}
          type="button"
          onClick={() => onSuggest(text)}
          className="inline-flex max-w-full items-start gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-left text-[13px] text-gray-700 transition-colors hover:border-primary/40 hover:bg-primary-soft hover:text-primary-strong focus-visible:outline-2 focus-visible:outline-primary"
        >
          <SubdirectoryArrowRightRounded
            sx={{ fontSize: 15 }}
            className="mt-px shrink-0 text-gray-400"
            aria-hidden="true"
          />
          <span className="min-w-0">{text}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * One entry of the conversation: the user's question (grey pill on the right), the assistant's
 * reply (light markdown with the generative UI blocks of its [[ui:…]] lines, tools used, copy
 * and, on the last reply, the follow-up suggestions) or a failed turn (with retry).
 */
export default function AiMessage({
  message,
  onNavigate,
  onRetry,
  canRetry,
  variant = "panel",
  onSuggest,
  showSuggestions = false,
}) {
  const { t, i18n } = useTranslation("aiAssistant");
  const toolsCaption = useToolsCaption(message.toolsUsed);
  const isReply = message.role === "assistant";
  const parts = useMemo(
    () => (isReply ? splitReply(message.content) : []),
    [isReply, message.content]
  );
  const copyText = useMemo(
    () => (isReply ? stripMarkers(message.content) : ""),
    [isReply, message.content]
  );

  if (message.role === "user") {
    return (
      <div className="flex justify-end">
        <p className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl bg-gray-100 px-3.5 py-2 text-[14px] leading-relaxed text-gray-900">
          {message.content}
        </p>
      </div>
    );
  }

  if (message.role === "error") {
    // The server message is in Italian: show it as is only to Italian users
    const isItalian = (i18n.resolvedLanguage || i18n.language || "it").startsWith("it");
    const text =
      (isItalian && message.serverMessage) ||
      t(`errors.${message.code}`, { defaultValue: t("errors.generic") });
    return (
      <div
        role="alert"
        className="flex items-start gap-2 rounded-xl border border-danger/20 bg-danger-soft px-3 py-2.5 text-[13px] text-danger-strong"
      >
        <ErrorOutlineRounded sx={{ fontSize: 18 }} className="mt-px shrink-0" />
        <div className="min-w-0 flex-1">
          <p>{text}</p>
          {canRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="mt-1.5 inline-flex items-center gap-1 rounded-md font-medium text-danger-strong underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-danger"
            >
              <ReplayRounded sx={{ fontSize: 16 }} />
              {t("errors.retry")}
            </button>
          )}
        </div>
      </div>
    );
  }

  const blocks = message.ui && typeof message.ui === "object" ? message.ui : {};
  const suggestions = Array.isArray(message.suggestions) ? message.suggestions : [];
  const textClass =
    variant === "page"
      ? "text-[15px] leading-relaxed text-gray-800"
      : "text-[14px] leading-relaxed text-gray-800";

  return (
    <div className="hover-scope group">
      {toolsCaption && <p className="mb-1 text-[12px] text-gray-400">{toolsCaption}</p>}
      <div className="space-y-3">
        {parts.map((part, index) =>
          part.type === "ui" && !blocks[part.ref] ? (
            // Data dropped from the stored conversation (storage full): say so, not a silent gap
            <p
              key={`${part.ref}-${index}`}
              className="rounded-lg border border-dashed border-gray-200 px-3 py-2 text-[12px] text-gray-500"
            >
              {t("blocks.unavailable")}
            </p>
          ) : part.type === "ui" ? (
            <AiBlock
              key={`${part.ref}-${index}`}
              block={blocks[part.ref]}
              view={part.view}
              variant={variant}
              onNavigate={onNavigate}
            />
          ) : (
            <AiMarkdown
              key={`text-${index}`}
              text={part.text}
              onNavigate={onNavigate}
              className={textClass}
            />
          )
        )}
      </div>
      {copyText && (
        <div className="mt-1 flex items-center">
          <CopyButton text={copyText} />
        </div>
      )}
      {showSuggestions && onSuggest && suggestions.length > 0 && (
        <Suggestions items={suggestions} onSuggest={onSuggest} />
      )}
    </div>
  );
}
